import { useQuery } from '@apollo/client'
import {
    capitalize,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useState } from 'react'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import CompaniesAutocompleteMultiple from 'shared/components/CompaniesAutocomplete/CompaniesAutocompleteMultiple'
import { getPxAsRem } from 'shared/utils'
import { fontSize } from 'shared/style/constants'
import { useTheme } from '@emotion/react'
import { Accordion } from 'shared/components/Accordion'

const experienceTimeFrameOptions = ['any', 'current', 'former', 'range']

interface CompanyFilterProps {
    companyIds: string[]
    companyTimeFrame?: string
    companyDateRange?: { gte?: string; lte?: string }
    onChange: (filters: {
        companyIds?: string[]
        companyTimeFrame?: string
        companyDateRange?: { gte?: string; lte?: string }
    }) => void
}

export function CompanyFilter({
    companyIds,
    companyTimeFrame,
    companyDateRange,
    onChange,
}: CompanyFilterProps) {
    const theme = useTheme()
    const [rangeError, setRangeError] = useState<string | null>(null)

    // Parse YYYY-MM format to Date object
    const parseYYYYMM = (yyyymm: string | undefined): Date | null => {
        if (!yyyymm) return null
        const [year, month] = yyyymm.split('-')
        // Create date on the 1st of the month
        return new Date(parseInt(year), parseInt(month) - 1, 1)
    }

    const timeFrame = companyTimeFrame || 'any'
    const startDate = parseYYYYMM(companyDateRange?.gte)
    const endDate = parseYYYYMM(companyDateRange?.lte)

    const { data: companiesData } = useQuery(GetCompaniesById, {
        variables: { ids: companyIds },
        skip: companyIds.length === 0,
    })

    const selectedCompanies = companiesData?.companiesById || []

    const isValidDate = (date: Date | null): date is Date => {
        return date !== null && date instanceof Date && !isNaN(date.getTime())
    }

    const formatDateToYYYYMM = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        return `${year}-${month}`
    }

    const handleCompanyChange = (
        _: any,
        newCompanies: typeof selectedCompanies
    ) => {
        // Build date range for 'range' timeframe if any dates are valid
        let dateRange: { gte?: string; lte?: string } | undefined = undefined
        if (timeFrame === 'range') {
            const hasStart = isValidDate(startDate)
            const hasEnd = isValidDate(endDate)
            if (hasStart || hasEnd) {
                dateRange = {
                    gte: hasStart ? formatDateToYYYYMM(startDate) : undefined,
                    lte: hasEnd ? formatDateToYYYYMM(endDate) : undefined,
                }
            }
        }

        console.log('🔍 CompanyFilter handleCompanyChange:', {
            timeFrame,
            dateRange,
            companyIds: newCompanies.map((c) => c.id),
        })

        onChange({
            companyIds: newCompanies.map((c) => c.id),
            companyTimeFrame: timeFrame === 'any' ? undefined : timeFrame,
            companyDateRange: dateRange,
        })
    }

    const handleTimeFrameChange = (
        _: React.ChangeEvent<HTMLInputElement>,
        selectedTimeFrame: string
    ) => {
        // Build date range for 'range' timeframe if any dates are valid
        let dateRange: { gte?: string; lte?: string } | undefined = undefined
        if (selectedTimeFrame === 'range') {
            const hasStart = isValidDate(startDate)
            const hasEnd = isValidDate(endDate)
            if (hasStart || hasEnd) {
                dateRange = {
                    gte: hasStart ? formatDateToYYYYMM(startDate) : undefined,
                    lte: hasEnd ? formatDateToYYYYMM(endDate) : undefined,
                }
            }
        }

        onChange({
            companyIds,
            companyTimeFrame:
                selectedTimeFrame === 'any' ? undefined : selectedTimeFrame,
            companyDateRange: dateRange,
        })
    }

    const handleDateChange = (
        rangeKey: 'gte' | 'lte',
        newDate: Date | null
    ) => {
        // Update local state
        const newStartDate = rangeKey === 'gte' ? newDate : startDate
        const newEndDate = rangeKey === 'lte' ? newDate : endDate

        // Validate date order if both are valid
        if (
            isValidDate(newStartDate) &&
            isValidDate(newEndDate) &&
            newStartDate > newEndDate
        ) {
            setRangeError('End date must be after start date')
            return
        }

        setRangeError(null)

        // Build partial or complete date range based on valid dates
        const hasStartDate = isValidDate(newStartDate)
        const hasEndDate = isValidDate(newEndDate)

        // If neither date is valid, clear the range entirely
        if (!hasStartDate && !hasEndDate) {
            onChange({
                companyIds,
                companyTimeFrame: timeFrame === 'any' ? undefined : timeFrame,
                companyDateRange: undefined,
            })
            return
        }

        // Send whatever dates are valid (partial or complete range)
        const dateRange = {
            gte: hasStartDate ? formatDateToYYYYMM(newStartDate) : undefined,
            lte: hasEndDate ? formatDateToYYYYMM(newEndDate) : undefined,
        }

        console.log('🔍 CompanyFilter handleDateChange:', {
            timeFrame,
            dateRange,
            hasStartDate,
            hasEndDate,
        })

        onChange({
            companyIds,
            companyTimeFrame: timeFrame === 'any' ? undefined : timeFrame,
            companyDateRange: dateRange,
        })
    }

    const hasActiveFilters =
        companyIds.length > 0 ||
        companyTimeFrame !== undefined ||
        companyDateRange !== undefined

    return (
        <Accordion
            isExpandedByDefault={hasActiveFilters}
            label={
                <Typography>
                    Company Experience{hasActiveFilters && ' •'}
                </Typography>
            }
        >
            <Stack spacing={2}>
                <CompaniesAutocompleteMultiple
                    value={selectedCompanies}
                    onChange={handleCompanyChange}
                    label="Company"
                    placeholder="Select companies..."
                />

                <RadioGroup
                    value={timeFrame}
                    onChange={handleTimeFrameChange}
                    row
                >
                    {experienceTimeFrameOptions.map((value) => (
                        <FormControlLabel
                            control={
                                <Radio
                                    size="small"
                                    sx={{
                                        padding: getPxAsRem(4),
                                        marginLeft: getPxAsRem(5),
                                    }}
                                />
                            }
                            key={value}
                            label={capitalize(value)}
                            value={value}
                            slotProps={{
                                typography: { fontSize: fontSize.small },
                            }}
                            sx={{ marginRight: getPxAsRem(10) }}
                        />
                    ))}
                </RadioGroup>

                <Stack gap={getPxAsRem(5)} direction="row">
                    <DatePicker
                        label="Start Date"
                        onChange={(date) => handleDateChange('gte', date)}
                        format="MM/yyyy"
                        disabled={timeFrame !== 'range'}
                        sx={{ width: getPxAsRem(200) }}
                        value={startDate}
                        maxDate={new Date()}
                        slotProps={{
                            textField: {
                                error: !!rangeError,
                            },
                        }}
                    />
                    <DatePicker
                        label="End Date"
                        onChange={(date) => handleDateChange('lte', date)}
                        format="MM/yyyy"
                        disabled={timeFrame !== 'range'}
                        sx={{ width: getPxAsRem(200) }}
                        value={endDate}
                        maxDate={new Date()}
                        slotProps={{
                            textField: {
                                error: !!rangeError,
                            },
                        }}
                    />
                </Stack>

                {rangeError && (
                    <Typography
                        variant="body2"
                        color={theme.palette.status.red}
                    >
                        {rangeError}
                    </Typography>
                )}
            </Stack>
        </Accordion>
    )
}
